import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { debugError } from '../utils/debugLog';

// Error types for classification
export type ErrorType = 'network' | 'validation' | 'notFound' | 'server' | 'permission' | 'timeout' | 'generic';
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorContext = 'schedule' | 'shows' | 'audio' | 'store' | 'cart' | 'checkout' | 'global';

// Error entry interface
export interface ErrorEntry {
  id: string;
  type: ErrorType;
  severity: ErrorSeverity;
  context: ErrorContext;
  message: string;
  originalError?: Error;
  timestamp: Date;
  resolved: boolean;
  retryCount: number;
  metadata?: Record<string, any>;
}

// Error analytics interface
export interface ErrorAnalytics {
  totalErrors: number;
  errorsByType: Record<ErrorType, number>;
  errorsBySeverity: Record<ErrorSeverity, number>;
  errorsByContext: Record<ErrorContext, number>;
  recentErrors: ErrorEntry[];
  errorRate: number; // errors per minute
  mostCommonError: ErrorType | null;
  criticalErrorCount: number;
}

// Error state interface
export interface ErrorState {
  errors: ErrorEntry[];
  analytics: ErrorAnalytics;
  isGlobalErrorActive: boolean;
  lastErrorTime: Date | null;
}

// Action types
type ErrorAction =
  | { type: 'REPORT_ERROR'; payload: Omit<ErrorEntry, 'id' | 'timestamp' | 'resolved' | 'retryCount'> }
  | { type: 'RESOLVE_ERROR'; payload: { id: string } }
  | { type: 'RETRY_ERROR'; payload: { id: string } }
  | { type: 'CLEAR_ERRORS'; payload?: { context?: ErrorContext; type?: ErrorType } }
  | { type: 'CLEAR_ALL_ERRORS' };

// Initial state
const initialState: ErrorState = {
  errors: [],
  analytics: {
    totalErrors: 0,
    errorsByType: {
      network: 0,
      validation: 0,
      notFound: 0,
      server: 0,
      permission: 0,
      timeout: 0,
      generic: 0,
    },
    errorsBySeverity: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    },
    errorsByContext: {
      schedule: 0,
      shows: 0,
      audio: 0,
      store: 0,
      cart: 0,
      checkout: 0,
      global: 0,
    },
    recentErrors: [],
    errorRate: 0,
    mostCommonError: null,
    criticalErrorCount: 0,
  },
  isGlobalErrorActive: false,
  lastErrorTime: null,
};

// Utility functions
const generateErrorId = (): string => {
  return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const calculateErrorRate = (errors: ErrorEntry[]): number => {
  const now = new Date();
  const oneMinuteAgo = new Date(now.getTime() - 60 * 1000);
  const recentErrors = errors.filter(error => error.timestamp >= oneMinuteAgo);
  return recentErrors.length;
};

const getMostCommonError = (errorsByType: Record<ErrorType, number>): ErrorType | null => {
  const entries = Object.entries(errorsByType) as [ErrorType, number][];
  const maxEntry = entries.reduce((max, current) => 
    current[1] > max[1] ? current : max, ['generic', 0] as [ErrorType, number]
  );
  return maxEntry[1] > 0 ? maxEntry[0] : null;
};

const updateAnalytics = (errors: ErrorEntry[]): ErrorAnalytics => {
  const analytics: ErrorAnalytics = {
    totalErrors: errors.length,
    errorsByType: {
      network: 0,
      validation: 0,
      notFound: 0,
      server: 0,
      permission: 0,
      timeout: 0,
      generic: 0,
    },
    errorsBySeverity: {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
    },
    errorsByContext: {
      schedule: 0,
      shows: 0,
      audio: 0,
      store: 0,
      cart: 0,
      checkout: 0,
      global: 0,
    },
    recentErrors: [],
    errorRate: 0,
    mostCommonError: null,
    criticalErrorCount: 0,
  };

  // Calculate analytics
  errors.forEach(error => {
    analytics.errorsByType[error.type]++;
    analytics.errorsBySeverity[error.severity]++;
    analytics.errorsByContext[error.context]++;
    
    if (error.severity === 'critical') {
      analytics.criticalErrorCount++;
    }
  });

  // Recent errors (last 10)
  analytics.recentErrors = errors
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    .slice(0, 10);

  // Error rate and most common error
  analytics.errorRate = calculateErrorRate(errors);
  analytics.mostCommonError = getMostCommonError(analytics.errorsByType);

  return analytics;
};

// Reducer
const errorReducer = (state: ErrorState, action: ErrorAction): ErrorState => {
  switch (action.type) {
    case 'REPORT_ERROR': {
      const newError: ErrorEntry = {
        ...action.payload,
        id: generateErrorId(),
        timestamp: new Date(),
        resolved: false,
        retryCount: 0,
      };

      const updatedErrors = [...state.errors, newError];
      const analytics = updateAnalytics(updatedErrors);
      
      return {
        ...state,
        errors: updatedErrors,
        analytics,
        isGlobalErrorActive: newError.severity === 'critical' || analytics.criticalErrorCount > 0,
        lastErrorTime: newError.timestamp,
      };
    }

    case 'RESOLVE_ERROR': {
      const updatedErrors = state.errors.map(error =>
        error.id === action.payload.id ? { ...error, resolved: true } : error
      );
      const analytics = updateAnalytics(updatedErrors);
      
      // Check for unresolved critical errors only
      const unresolvedCriticalErrors = updatedErrors.filter(error => 
        error.severity === 'critical' && !error.resolved
      );

      return {
        ...state,
        errors: updatedErrors,
        analytics,
        isGlobalErrorActive: unresolvedCriticalErrors.length > 0,
      };
    }

    case 'RETRY_ERROR': {
      const updatedErrors = state.errors.map(error =>
        error.id === action.payload.id 
          ? { ...error, retryCount: error.retryCount + 1 }
          : error
      );
      const analytics = updateAnalytics(updatedErrors);

      return {
        ...state,
        errors: updatedErrors,
        analytics,
      };
    }

    case 'CLEAR_ERRORS': {
      let filteredErrors = state.errors;
      
      if (action.payload?.context) {
        filteredErrors = filteredErrors.filter(error => error.context !== action.payload!.context);
      }
      
      if (action.payload?.type) {
        filteredErrors = filteredErrors.filter(error => error.type !== action.payload!.type);
      }

      const analytics = updateAnalytics(filteredErrors);
      
      // Check for unresolved critical errors only
      const unresolvedCriticalErrors = filteredErrors.filter(error => 
        error.severity === 'critical' && !error.resolved
      );

      return {
        ...state,
        errors: filteredErrors,
        analytics,
        isGlobalErrorActive: unresolvedCriticalErrors.length > 0,
      };
    }

    case 'CLEAR_ALL_ERRORS': {
      return {
        ...initialState,
      };
    }

    default:
      return state;
  }
};

// Context interface
export interface GlobalErrorContextValue {
  // State
  errors: ErrorEntry[];
  analytics: ErrorAnalytics;
  isGlobalErrorActive: boolean;
  lastErrorTime: Date | null;
  
  // Actions
  reportError: (error: Omit<ErrorEntry, 'id' | 'timestamp' | 'resolved' | 'retryCount'>) => void;
  resolveError: (id: string) => void;
  retryError: (id: string) => void;
  clearErrors: (options?: { context?: ErrorContext; type?: ErrorType }) => void;
  clearAllErrors: () => void;
  
  // Utility functions
  getErrorsByContext: (context: ErrorContext) => ErrorEntry[];
  getErrorsByType: (type: ErrorType) => ErrorEntry[];
  getUnresolvedErrors: () => ErrorEntry[];
  getCriticalErrors: () => ErrorEntry[];
  hasActiveErrors: (context?: ErrorContext) => boolean;
}

// Create context
const GlobalErrorContext = createContext<GlobalErrorContextValue | undefined>(undefined);

// Provider component
export interface GlobalErrorProviderProps {
  children: ReactNode;
  onError?: (error: ErrorEntry) => void;
  maxErrors?: number;
}

export const GlobalErrorProvider: React.FC<GlobalErrorProviderProps> = ({
  children,
  onError,
  maxErrors = 100,
}) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Actions
  const reportError = useCallback((error: Omit<ErrorEntry, 'id' | 'timestamp' | 'resolved' | 'retryCount'>) => {
    dispatch({ type: 'REPORT_ERROR', payload: error });
    
    // Call external error handler if provided
    if (onError) {
      const errorEntry: ErrorEntry = {
        ...error,
        id: generateErrorId(),
        timestamp: new Date(),
        resolved: false,
        retryCount: 0,
      };
      onError(errorEntry);
    }

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      debugError(`[GlobalError] ${error.context}:${error.type}:${error.severity}`, error.message, error.originalError);
    }
  }, [onError]);

  const resolveError = useCallback((id: string) => {
    dispatch({ type: 'RESOLVE_ERROR', payload: { id } });
  }, []);

  const retryError = useCallback((id: string) => {
    dispatch({ type: 'RETRY_ERROR', payload: { id } });
  }, []);

  const clearErrors = useCallback((options?: { context?: ErrorContext; type?: ErrorType }) => {
    dispatch({ type: 'CLEAR_ERRORS', payload: options });
  }, []);

  const clearAllErrors = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' });
  }, []);

  // Utility functions
  const getErrorsByContext = useCallback((context: ErrorContext): ErrorEntry[] => {
    return state.errors.filter(error => error.context === context);
  }, [state.errors]);

  const getErrorsByType = useCallback((type: ErrorType): ErrorEntry[] => {
    return state.errors.filter(error => error.type === type);
  }, [state.errors]);

  const getUnresolvedErrors = useCallback((): ErrorEntry[] => {
    return state.errors.filter(error => !error.resolved);
  }, [state.errors]);

  const getCriticalErrors = useCallback((): ErrorEntry[] => {
    return state.errors.filter(error => error.severity === 'critical' && !error.resolved);
  }, [state.errors]);

  const hasActiveErrors = useCallback((context?: ErrorContext): boolean => {
    const unresolvedErrors = getUnresolvedErrors();
    if (context) {
      return unresolvedErrors.some(error => error.context === context);
    }
    return unresolvedErrors.length > 0;
  }, [getUnresolvedErrors]);

  // Cleanup old errors if we exceed maxErrors
  React.useEffect(() => {
    if (state.errors.length > maxErrors) {
      const sortedErrors = [...state.errors].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      const errorsToKeep = sortedErrors.slice(0, maxErrors);
      
      // Only keep recent errors, but preserve critical unresolved errors
      const criticalUnresolved = state.errors.filter(error => 
        error.severity === 'critical' && !error.resolved
      );
      
      const finalErrors = Array.from(new Set([...errorsToKeep, ...criticalUnresolved]));
      
      if (finalErrors.length !== state.errors.length) {
        dispatch({ type: 'CLEAR_ALL_ERRORS' });
        finalErrors.forEach(error => {
          dispatch({ 
            type: 'REPORT_ERROR', 
            payload: {
              type: error.type,
              severity: error.severity,
              context: error.context,
              message: error.message,
              originalError: error.originalError,
              metadata: error.metadata,
            }
          });
        });
      }
    }
  }, [state.errors, maxErrors]);

  const contextValue: GlobalErrorContextValue = {
    // State
    errors: state.errors,
    analytics: state.analytics,
    isGlobalErrorActive: state.isGlobalErrorActive,
    lastErrorTime: state.lastErrorTime,
    
    // Actions
    reportError,
    resolveError,
    retryError,
    clearErrors,
    clearAllErrors,
    
    // Utility functions
    getErrorsByContext,
    getErrorsByType,
    getUnresolvedErrors,
    getCriticalErrors,
    hasActiveErrors,
  };

  return (
    <GlobalErrorContext.Provider value={contextValue}>
      {children}
    </GlobalErrorContext.Provider>
  );
};

// Hook to use the context
export const useGlobalError = (): GlobalErrorContextValue => {
  const context = useContext(GlobalErrorContext);
  if (context === undefined) {
    throw new Error('useGlobalError must be used within a GlobalErrorProvider');
  }
  return context;
};

// Convenience hooks for specific contexts
export const useScheduleErrors = () => {
  const { getErrorsByContext, hasActiveErrors, clearErrors } = useGlobalError();
  return {
    errors: getErrorsByContext('schedule'),
    hasErrors: hasActiveErrors('schedule'),
    clearErrors: () => clearErrors({ context: 'schedule' }),
  };
};

export const useShowsErrors = () => {
  const { getErrorsByContext, hasActiveErrors, clearErrors } = useGlobalError();
  return {
    errors: getErrorsByContext('shows'),
    hasErrors: hasActiveErrors('shows'),
    clearErrors: () => clearErrors({ context: 'shows' }),
  };
};

export const useAudioErrors = () => {
  const { getErrorsByContext, hasActiveErrors, clearErrors } = useGlobalError();
  return {
    errors: getErrorsByContext('audio'),
    hasErrors: hasActiveErrors('audio'),
    clearErrors: () => clearErrors({ context: 'audio' }),
  };
};

export const useStoreErrors = () => {
  const { getErrorsByContext, hasActiveErrors, clearErrors } = useGlobalError();
  return {
    errors: getErrorsByContext('store'),
    hasErrors: hasActiveErrors('store'),
    clearErrors: () => clearErrors({ context: 'store' }),
  };
};

export default GlobalErrorContext;
